import type { Screen } from '@floorpoll/shared';
import { countQuestions, readScreens, slugFor } from './screenReadiness.js';

/** The id a rail entry jumps to. Owned here so both sides agree on the format. */
export const screenCardId = (screenId: string) => `screen-card-${screenId}`;

interface Props {
  screens: Screen[];
}

/**
 * The spine of the prompt book: the poll's running order, indexed and marked up.
 *
 * Navigation, never a second copy of the editor — every screen stays mounted in
 * the canvas beside it. That is a requirement, not a layout preference: a
 * question can step into a neighbouring screen (`questionMoves.ts`), and the
 * author has to watch it land somewhere that exists.
 *
 * The marks read as pre-flight, so they answer "can this go in front of a room"
 * — which is stricter than "will this save". An empty screen is a legal draft
 * and saves fine; it is still marked unready, because presenting is what the
 * author is heading for.
 */
export function RunningOrder({ screens }: Props) {
  const readiness = readScreens(screens);

  function jumpTo(screenId: string) {
    const card = document.getElementById(screenCardId(screenId));
    if (!card) return;

    // Scroll and focus are separate on purpose: focusing alone jumps hard, and
    // letting focus scroll as well fights the smooth scroll already running.
    const reduced = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    card.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    card.focus({ preventScroll: true });
  }

  return (
    <nav className="running-order" aria-label="Running order">
      <p className="eyebrow">Running order</p>

      {screens.length === 0 ? (
        <p className="running-order-empty muted">
          Nothing to run yet. Add a screen and it shows up here.
        </p>
      ) : (
        <ol className="cue-list">
          {screens.map((screen, index) => {
            const entry = readiness[index];
            const slug = slugFor(screen);
            return (
              <li key={screen.screenId}>
                <button
                  type="button"
                  // `cue-row`, not `cue`: the presenter's screen-position dots
                  // already own the bare class.
                  className={`cue-row cue-row-${entry?.state ?? 'ready'}`}
                  onClick={() => jumpTo(screen.screenId)}
                >
                  {/* The mark and the number are decoration over the label
                      below, which already carries both in words. */}
                  <span className="cue-mark" aria-hidden="true" />
                  <span className="cue-number" aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="cue-body">
                    <span className="cue-slug">{slug ?? `Screen ${index + 1}`}</span>
                    <span className="cue-detail">{entry?.detail}</span>
                  </span>
                  {/* Adds only what the visible text cannot say — where this
                      goes. Repeating the detail here would read it twice, the
                      same double-announce the join code's hidden label avoids. */}
                  <span className="visually-hidden">Go to screen {index + 1}</span>
                </button>
              </li>
            );
          })}
        </ol>
      )}

      <p className="running-order-tally muted">
        {screens.length === 1 ? '1 screen' : `${screens.length} screens`} ·{' '}
        {countQuestions(screens) === 1 ? '1 question' : `${countQuestions(screens)} questions`}
      </p>
    </nav>
  );
}
